import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreatePostRequest } from "../../entry/server/post.ts";
import { postRepo } from "../../repo/post-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";
import PostFormFields, { emptyPostForm, isPostFormValid } from "./post-form-fields.tsx";

export default function NewPost() {
  const navigate = useNavigate();
  const { setMessage } = useContext(AppContext);
  const [form, setForm] = useState(emptyPostForm);
  const [thumbnailError, setThumbnailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const thumbnailPreviewUrl = useMemo(
    () => (form.thumbnailFile ? URL.createObjectURL(form.thumbnailFile) : ""),
    [form.thumbnailFile]
  );

  useEffect(() => {
    return () => {
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl);
      }
    };
  }, [thumbnailPreviewUrl]);

  const onThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setThumbnailError("فقط فایل تصویری مجاز است.");
      return;
    }

    setThumbnailError("");
    setForm((prev) => ({ ...prev, thumbnailFile: file }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isPostFormValid(form, "create") || !form.thumbnailFile) {
      setMessage({ type: "error", message: "لطفاً همه فیلدهای الزامی را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CreatePostRequest = {
        title: form.title.trim(),
        content: form.content,
        is_published: form.isPublished ? "true" : "false",
        thumbnail: form.thumbnailFile,
      };

      const response = await postRepo.createPost(payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ایجاد پست ناموفق بود." });
        return;
      }

      setMessage({ type: "success", message: "پست با موفقیت ایجاد شد." });
      navigate("/admin/posts");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ایجاد پست" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <PostFormFields
            mode="create"
            title={form.title}
            content={form.content}
            isPublished={form.isPublished}
            thumbnailFile={form.thumbnailFile}
            thumbnailPreviewUrl={thumbnailPreviewUrl}
            thumbnailError={thumbnailError}
            onTitleChange={(value) => setForm((prev) => ({ ...prev, title: value }))}
            onContentChange={(value) => setForm((prev) => ({ ...prev, content: value }))}
            onPublishedChange={(value) => setForm((prev) => ({ ...prev, isPublished: value }))}
            onThumbnailChange={onThumbnailChange}
          />
          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isPostFormValid(form, "create") || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ایجاد…" : "ایجاد پست"}
            </button>
            <Link
              to="/admin/posts"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
